import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { differenceInDays } from 'date-fns';
import LocationAutocomplete from './LocationAutocomplete';
import { SingleImageUploader } from './ImageUploader';
import { DOCUMENT, documentFormLabelSx } from '../constants/documentTheme';
import { CURRENCIES } from '../utils/currencyUtils';
import { getTripLocationsArray } from '../utils/tripUtils';
import { partnerOxbloodBtnSx, partnerLinkSx } from './PartnerListChrome';
import api from '../config/api';

const TRIP_TYPE_VALUES = ['leisure', 'conference', 'business', 'celebration', 'adventure'];

const underlineSx = {
  '& .MuiInput-underline:before': { borderBottomColor: DOCUMENT.ruleHard },
  '& .MuiInput-underline:hover:not(.Mui-disabled):before': { borderBottomColor: DOCUMENT.accent },
  '& .MuiInput-underline:after': { borderBottomColor: DOCUMENT.accent },
  '& .MuiInputBase-root': { pl: 0, pr: 0, width: '100%' },
  '& .MuiInputBase-input': {
    fontSize: 16,
    py: 1.25,
    pl: 0,
    pr: 0,
    color: DOCUMENT.ink,
    lineHeight: 1.5,
    overflow: 'visible',
  },
  '& .MuiInputBase-input::placeholder': { color: DOCUMENT.placeholder, opacity: 1 },
};

const ghostSx = {
  background: 'none',
  border: 0,
  p: 0,
  font: 'inherit',
  fontSize: 12,
  fontWeight: 600,
  color: DOCUMENT.ink3,
  cursor: 'pointer',
  borderBottom: '1px solid transparent',
  '&:hover': { color: DOCUMENT.ink, borderBottomColor: DOCUMENT.accent },
};

const Field = ({ label, required, wide, children }) => (
  <Box sx={{ py: 2.1, borderBottom: `1px solid ${DOCUMENT.rule}`, gridColumn: wide ? '1 / -1' : 'auto' }}>
    <Typography sx={documentFormLabelSx}>
      {label}
      {required && <Box component="span" sx={{ color: DOCUMENT.accent, ml: 0.4 }}>*</Box>}
    </Typography>
    {children}
  </Box>
);

const toDateInput = (value) => {
  if (!value) return '';
  const raw = String(value);
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 10);
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
};

const normalizeTripTypeForForm = (tripType) => {
  if (!tripType) return '';
  if (tripType === 'vacation') return 'leisure';
  return TRIP_TYPE_VALUES.includes(tripType) ? tripType : '';
};

const normalizeTripTypeForApi = (tripType) => {
  if (!tripType) return 'vacation';
  if (tripType === 'leisure') return 'vacation';
  return tripType;
};

const parseLocations = (trip) => {
  if (!trip) return [];
  if (typeof trip.locations === 'string') {
    try {
      const parsed = JSON.parse(trip.locations);
      if (Array.isArray(parsed)) {
        return parsed.filter((loc) => loc?.name && String(loc.name).trim());
      }
    } catch (err) {
      // fall through
    }
  }
  return getTripLocationsArray(trip);
};

const buildInitialForm = (trip) => {
  const locations = parseLocations(trip);
  return {
    title: trip?.title || '',
    description: trip?.description || '',
    locations: locations.length ? locations.map((loc) => ({
      name: loc.name || '',
      coordinates: loc.coordinates || null,
      placeId: loc.placeId || null,
    })) : [{ name: '', coordinates: null }],
    startDate: toDateInput(trip?.start_date),
    endDate: toDateInput(trip?.end_date),
    tripType: normalizeTripTypeForForm(trip?.trip_type),
    budget: trip?.budget != null && trip.budget !== '' ? String(trip.budget) : '',
    budget_currency: trip?.budget_currency || 'USD',
    image_url: trip?.image_url || '',
  };
};

/**
 * Partner dialog to edit the same Create Trip fields (minus client email).
 * mode: 'client' → PUT /trips/:id ; 'group' → PUT /partners/master-trips/:id
 */
const PartnerTripDetailsEditor = ({
  open,
  onClose,
  trip,
  mode = 'client',
  onSaved,
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState(() => buildInitialForm(trip));
  const [validationErrors, setValidationErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || !trip) return;
    setFormData(buildInitialForm(trip));
    setValidationErrors({});
    setError('');
    // Only rehydrate when the dialog opens or the trip id changes — not on every
    // parent re-render, which would wipe an in-progress cover upload.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, trip?.id]);

  const nightsHint = useMemo(() => {
    if (!formData.startDate || !formData.endDate) return '';
    const nights = differenceInDays(new Date(formData.endDate), new Date(formData.startDate));
    if (nights < 0) return '';
    return t('partnerLists.nights', { count: nights });
  }, [formData.startDate, formData.endDate, t]);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    const field = name || e.target.getAttribute?.('name');
    if (!field) return;
    setFormData((prev) => ({ ...prev, [field]: value }));
    setValidationErrors((prev) => (prev[field] ? { ...prev, [field]: '' } : prev));
  }, []);

  const handleLocationChange = useCallback((index, newValue) => {
    setFormData((prev) => ({
      ...prev,
      locations: prev.locations.map((loc, i) => (i === index ? { ...loc, name: newValue } : loc)),
    }));
  }, []);

  const handleLocationSelect = useCallback((index, placeData) => {
    setFormData((prev) => ({
      ...prev,
      locations: prev.locations.map((loc, i) => (
        i === index ? {
          name: placeData.address,
          coordinates: placeData.coordinates,
          placeId: placeData.placeId || null,
        } : loc
      )),
    }));
  }, []);

  const addLocation = useCallback(() => {
    setFormData((prev) => ({
      ...prev,
      locations: [...prev.locations, { name: '', coordinates: null }],
    }));
  }, []);

  const removeLocation = useCallback((index) => {
    setFormData((prev) => ({
      ...prev,
      locations: prev.locations.filter((_, i) => i !== index),
    }));
  }, []);

  const validate = useCallback(() => {
    const errors = {};
    if (!formData.title.trim()) errors.title = t('createTrip.tripNameRequired', { defaultValue: 'Trip name is required' });
    const validLocations = formData.locations.filter((loc) => loc.name && loc.name.trim());
    if (!validLocations.length) errors.locations = t('createTrip.destinationRequired', { defaultValue: 'At least one destination is required' });
    if (!formData.startDate) errors.startDate = t('createTrip.startDateRequired', { defaultValue: 'Start date is required' });
    if (!formData.endDate) errors.endDate = t('createTrip.endDateRequired', { defaultValue: 'End date is required' });
    if (formData.startDate && formData.endDate && formData.startDate > formData.endDate) {
      errors.endDate = t('createTrip.endBeforeStart', { defaultValue: 'End date must be after start date' });
    }
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formData, t]);

  const handleSave = async () => {
    if (!trip?.id || !validate()) return;

    try {
      setSaving(true);
      setError('');
      const token = localStorage.getItem('partnerToken');
      const validLocations = formData.locations.filter((loc) => loc.name && loc.name.trim());
      const tripType = normalizeTripTypeForApi(formData.tripType);
      const budgetValue = formData.budget === '' ? null : Number(formData.budget);

      if (mode === 'group') {
        await api.put(`/partners/master-trips/${trip.id}`, {
          title: formData.title.trim(),
          description: formData.description,
          locations: validLocations,
          startDate: formData.startDate,
          endDate: formData.endDate,
          tripType,
          image_url: formData.image_url || null,
          budget: budgetValue,
          budget_currency: formData.budget_currency || 'USD',
        }, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } else {
        await api.put(`/trips/${trip.id}`, {
          title: formData.title.trim(),
          description: formData.description,
          locations: validLocations,
          start_date: formData.startDate,
          end_date: formData.endDate,
          trip_type: tripType,
          image_url: formData.image_url || null,
          budget: budgetValue,
          budget_currency: formData.budget_currency || 'USD',
        }, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }

      onSaved?.();
      onClose?.();
    } catch (err) {
      setError(err.response?.data?.error || t('partnerManage.editDetailsFailed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      fullWidth
      maxWidth="md"
      disableEnforceFocus
      keepMounted={false}
    >
      <DialogTitle sx={{ fontFamily: DOCUMENT.display, fontWeight: 400 }}>
        {t('partnerManage.editDetailsTitle')}
      </DialogTitle>
      <DialogContent dividers>
        {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

        <Field label={t('createTrip.tripName')} required wide>
          <TextField
            name="title"
            value={formData.title}
            onChange={handleChange}
            fullWidth
            variant="standard"
            error={!!validationErrors.title}
            helperText={validationErrors.title}
            inputProps={{ 'aria-label': t('createTrip.tripName') }}
            sx={underlineSx}
          />
        </Field>

        <Field label={t('createTrip.destination')} required wide>
          {formData.locations.map((location, index) => (
            <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: index < formData.locations.length - 1 ? 1.5 : 0, width: '100%' }}>
              <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
                <LocationAutocomplete
                  value={location.name}
                  onChange={(newValue) => handleLocationChange(index, newValue)}
                  onPlaceSelect={(placeData) => handleLocationSelect(index, placeData)}
                  label=""
                  required={index === 0}
                  fullWidth
                  variant="standard"
                  placeholder={t('createTrip.destinationPlaceholder')}
                  sx={underlineSx}
                />
              </Box>
              {formData.locations.length > 1 && (
                <Box component="button" type="button" onClick={() => removeLocation(index)} sx={ghostSx}>
                  {t('createTrip.remove')}
                </Box>
              )}
            </Box>
          ))}
          {validationErrors.locations ? (
            <Typography variant="caption" color="error">{validationErrors.locations}</Typography>
          ) : null}
          <Box component="button" type="button" onClick={addLocation} sx={{ ...ghostSx, pt: 1.75 }}>
            {t('createTrip.addDestination')}
          </Box>
        </Field>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, columnGap: 3.25 }}>
          <Field label={t('createTrip.startDate')} required>
            <TextField
              name="startDate"
              type="date"
              value={formData.startDate}
              onChange={handleChange}
              fullWidth
              variant="standard"
              error={!!validationErrors.startDate}
              helperText={validationErrors.startDate}
              InputLabelProps={{ shrink: true }}
              inputProps={{ 'aria-label': t('createTrip.startDate') }}
              sx={underlineSx}
            />
          </Field>
          <Field label={t('createTrip.endDate')} required>
            <TextField
              name="endDate"
              type="date"
              value={formData.endDate}
              onChange={handleChange}
              fullWidth
              variant="standard"
              error={!!validationErrors.endDate}
              helperText={validationErrors.endDate || nightsHint || ' '}
              InputLabelProps={{ shrink: true }}
              inputProps={{ 'aria-label': t('createTrip.endDate') }}
              sx={underlineSx}
            />
          </Field>
        </Box>

        <Field label={t('createTrip.tripType')} wide>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, pt: 0.25 }}>
            {TRIP_TYPE_VALUES.map((type) => {
              const active = formData.tripType === type;
              return (
                <Box
                  key={type}
                  component="button"
                  type="button"
                  aria-pressed={active}
                  onClick={() => setFormData((prev) => ({ ...prev, tripType: active ? '' : type }))}
                  sx={{
                    background: active ? DOCUMENT.oxblood : 'none',
                    border: `1px solid ${active ? DOCUMENT.oxblood : DOCUMENT.ruleHard}`,
                    color: active ? DOCUMENT.ivory : DOCUMENT.ink2,
                    font: 'inherit',
                    fontSize: 12.5,
                    px: 1.85,
                    py: 1,
                    cursor: 'pointer',
                  }}
                >
                  {t(`createTrip.types.${type}`)}
                </Box>
              );
            })}
          </Box>
        </Field>

        <Field label={t('createTrip.description')} wide>
          <TextField
            name="description"
            value={formData.description}
            onChange={handleChange}
            fullWidth
            multiline
            minRows={2}
            variant="standard"
            placeholder={t('createTrip.descriptionPlaceholder')}
            inputProps={{ 'aria-label': t('createTrip.description') }}
            sx={underlineSx}
          />
        </Field>

        <Field label={t('createTrip.coverImage')} wide>
          <SingleImageUploader
            value={formData.image_url}
            onChange={(url) => setFormData((prev) => ({ ...prev, image_url: url || '' }))}
            preset="cover"
            folder="trips/covers"
            label={t('createTrip.coverBrowse')}
            height={140}
            cropAspect={16 / 9}
            cropShape="rect"
          />
          <Typography sx={{ fontSize: 12, color: DOCUMENT.ink3, mt: 1 }}>{t('createTrip.coverHint')}</Typography>
        </Field>

        <Field label={t('createTrip.budget')} wide>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 110px', gap: 2.25 }}>
            <TextField
              name="budget"
              type="number"
              value={formData.budget}
              onChange={handleChange}
              fullWidth
              variant="standard"
              placeholder="0"
              inputProps={{ min: 0, 'aria-label': t('createTrip.budget') }}
              sx={underlineSx}
            />
            <TextField
              select
              name="budget_currency"
              value={formData.budget_currency}
              onChange={handleChange}
              variant="standard"
              sx={underlineSx}
            >
              {CURRENCIES.filter((c) => ['USD', 'EUR', 'GBP', 'CAD', 'AUD'].includes(c.code))
                .concat([{ code: 'AED', name: 'UAE Dirham', symbol: 'AED' }])
                .map((currency) => (
                  <MenuItem key={currency.code} value={currency.code}>{currency.code}</MenuItem>
                ))}
            </TextField>
          </Box>
        </Field>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={saving} sx={partnerLinkSx}>
          {t('partnerLists.cancel')}
        </Button>
        <Button onClick={handleSave} disabled={saving} sx={partnerOxbloodBtnSx}>
          {saving ? <CircularProgress size={20} color="inherit" /> : t('partnerManage.saveDetails')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PartnerTripDetailsEditor;
